import { Alert, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { formatDate } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { SCHEDULES_PATH, type StaffSchedule, scheduleInForce, schedulesByStaff, summarize } from './schedule';
import { type PatternDay, WeeklyPatternEditor } from './WeeklyPatternEditor';

/** A week somebody else already works, offered as a starting point. */
interface CopyableWeek {
  staffId: string;
  name: string;
  pattern: PatternDay[];
}

/**
 * Where a working week is set, wherever the reader came from.
 *
 * One editor rather than one per screen: it is opened from the person's own page and from the
 * roster-wide list on the hours screen, and a schedule typed on one of them must be the same act
 * as a schedule typed on the other. It is mounted only while it is open, so the fields always
 * belong to the person whose name is on the title — state left behind from a previous person is
 * how somebody's Tuesday gets written onto somebody else.
 */
export function ScheduleEditorModal({
  staffId,
  staffName,
  currentPattern,
  initialEffectiveFrom,
  onClose,
}: {
  staffId: string;
  staffName: string;
  /** The week in force today, which the editor opens on. Empty for somebody who has never had one. */
  currentPattern: PatternDay[];
  /**
   * The date this week should count from, when the reader arrived from something dated.
   *
   * Today is the right default for a change agreed today and the wrong one for every other reason
   * this editor gets opened. The hours screen flags a person with no working week on a period that
   * has usually already been worked — and a week written from today leaves that period exactly as
   * it was: no pattern found, every day still flagged, overtime still zero, the same red alert with
   * the same button under it, and nothing anywhere saying that the date nobody touched is why.
   * Schedules are append-only and effective-dated precisely so the right act is available; this is
   * what offers it.
   */
  initialEffectiveFrom?: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const today = useIslandToday();
  const [pattern, setPattern] = useState<PatternDay[]>(currentPattern);
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(initialEffectiveFrom ?? today);
  const [note, setNote] = useState('');
  const [copiedFrom, setCopiedFrom] = useState<string | null>(null);

  /**
   * The date everything on this form is being written for — which is the one in the field, not the
   * one the reader arrived with: it is a form, and the point of the date being editable is that it
   * can be edited. It decides which week gets copied off somebody else, and whether the reader is
   * writing history or writing from today.
   */
  const writtenFor = (effectiveFrom ?? today).slice(0, 10);
  const backdated = writtenFor < today;

  /**
   * Everybody's schedules, which is one request whatever the size of the roster and is usually
   * already in hand — it is the same read the working-weeks list makes, under the same key.
   *
   * Three people work the same shift at this bar. Typing that week three times is three chances to
   * type it differently, and two of those three would then disagree with the rota by fifteen
   * minutes in a way nobody would ever go looking for.
   */
  const everyone = useQuery({
    queryKey: listKey(SCHEDULES_PATH),
    queryFn: () => rawApi<StaffSchedule[]>('GET', SCHEDULES_PATH),
  });

  /*
   * Read on the date being written for, not on today. Everyone else's history is effective-dated
   * too, so "the same week Maria works" means something different in June than it does in August —
   * and this editor now opens on a past date whenever the hours being approved are past. Copying
   * today's week onto a June row is how a week nobody worked in June gets written into June, on the
   * one screen whose whole job is to give that period something true to measure against.
   */
  const copyable: CopyableWeek[] = [];
  for (const [id, history] of schedulesByStaff(everyone.data ?? [])) {
    if (id === staffId) continue;
    const inForce = scheduleInForce(history, writtenFor);
    // Somebody whose fixed schedule has ENDED has a row in force holding no days at all, and
    // "copy nobody's week" is not a shortcut anyone was looking for.
    if (!inForce || inForce.pattern.length === 0) continue;
    copyable.push({ staffId: id, name: inForce.staffName ?? 'Unknown', pattern: inForce.pattern });
  }
  copyable.sort((a, b) => a.name.localeCompare(b.name));

  const save = useMutation({
    mutationFn: () =>
      rawApi('POST', SCHEDULES_PATH, {
        staffId,
        effectiveFrom: writtenFor,
        pattern,
        note: note || undefined,
      }),
    onSuccess: () => {
      // The prefix, deliberately: `['list', SCHEDULES_PATH]` covers this person's own history AND
      // the roster-wide read behind the working-weeks list. Whichever of the two opened this
      // editor, the other one is on screen a click away and must not still be showing the old week.
      void queryClient.invalidateQueries({ queryKey: listKey(SCHEDULES_PATH) });
      notifyDone('Schedule saved', `${staffName}'s working week is updated.`);
      onClose();
    },
    onError: (err) => notifyError(err, 'save this schedule'),
  });

  return (
    <Modal
      opened
      onClose={onClose}
      title={`${staffName}'s working week`}
      size="xl"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Tick the days they work and set the hours. Hours before the start date below keep using the previous
          schedule.
        </Text>
        {/*
          Somebody else's week, as a starting point rather than as a link between two people. It
          copies the days and times into the grid below and nothing more: the two schedules are
          separate records from that moment, so changing one bartender's Saturday later does not
          quietly move the other's. The bar runs two shapes of week across the whole team, and
          typing the second one out by hand was the reason the third person never got one at all.
        */}
        {copyable.length > 0 && (
          <Select
            label="Start from someone else's week"
            description={
              backdated
                ? `The week they were working on ${formatDate(writtenFor)} lands in the grid below, where you can change anything before saving`
                : 'Their days and times land in the grid below, where you can change anything before saving'
            }
            placeholder="Optional — pick a person"
            data={copyable.map((o) => ({ value: o.staffId, label: `${o.name} · ${summarize(o.pattern)}` }))}
            value={copiedFrom}
            searchable
            disabled={save.isPending}
            onChange={(id) => {
              const from = copyable.find((o) => o.staffId === id);
              if (!from) return;
              setCopiedFrom(id);
              // Day by day rather than by reference: the array behind this is the cached answer to
              // the roster's own request, and editing one of its rows in place would rewrite the
              // week shown against the person it was copied FROM.
              setPattern(from.pattern.map((day) => ({ ...day })));
            }}
          />
        )}
        <WeeklyPatternEditor value={pattern} onChange={setPattern} disabled={save.isPending} />
        <Group grow align="flex-start">
          <DateInput
            label="Start date"
            description="When this pattern takes effect"
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            valueFormat="D MMM YYYY"
          />
          <TextInput
            label="Note"
            description="Optional — why it changed"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
        </Group>
        {/* A start date already gone is normally a mistake worth querying and here it is usually
            the answer, so it is stated rather than warned about. The reader arrived from a period
            that has already been worked; a week starting today would leave that period exactly as
            it was, and nothing on the screen would say the untouched date was the reason. */}
        {backdated && (
          <Alert color="blue" variant="light" title={`Counting from ${formatDate(writtenFor)}`}>
            That day has passed, so every day from it onward is measured against this week — which is what
            lets a period already worked find a schedule for its days and work out its overtime. Anything
            earlier keeps whatever week was in force then. Hours already worked out do not change until you
            recalculate them under Approve hours.
          </Alert>
        )}
        {/* An empty pattern is a meaningful answer, not an unfinished form: it is how a fixed
            schedule ENDS when someone moves to on-call. Blocking it left the previous pattern in
            force indefinitely, so every later timesheet reported false absences against a week
            nobody works any more. It still deserves saying out loud, because unticking every day
            looks the same as not having started. */}
        {pattern.length === 0 && (
          <Alert color="yellow" variant="light" title="No days ticked">
            Saving this records that {staffName} no longer has a fixed schedule from the start date. Their
            hours will still be recorded — there will just be nothing to compare them against.
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            {pattern.length === 0 ? 'Save — no fixed schedule' : 'Save schedule'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
